import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { signOut } from '../lib/auth';
import { useToast } from '../hooks/useToast';
import { Button } from './ui/Button';

const AdminLayout: React.FC = () => {
  const { loading } = useAuth();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  interface NavLinkItem {
    to: string;
    label: string;
  }

  const adminNavLinks: NavLinkItem[] = [
    { to: '/admin', label: 'Therapists' },
  ];

  const handleAdminLogout = async () => {
    setIsLoggingOut(true);
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
      addToast({
        type: 'error',
        message: 'Logout failed. Please try again.',
      });
    } finally {
      setIsLoggingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F8FA]">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#7F56D9] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F8F8FA]">
      <header className="bg-white border-b border-[#EAECF0] fixed w-full top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center h-16">
            <div className="flex-shrink-0">
              <img
                src="/therapyos-logo.svg"
                alt="TherapyOS Logo"
                className="h-8"
                onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                  e.currentTarget.src = 'https://placehold.co/100x32/7F56D9/FFFFFF?text=Logo';
                  e.currentTarget.onerror = null;
                }}
              />
            </div>

            <div className="hidden md:flex items-center space-x-4">
              <nav className="flex space-x-8 mr-4">
                {adminNavLinks.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    className={({ isActive }) =>
                      `text-base font-medium py-2 border-b-2 ${
                        isActive
                          ? 'text-[#101828] border-[#7F56D9] font-semibold'
                          : 'text-[#667085] border-transparent hover:text-[#101828] hover:border-[#D0D5DD]'
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                ))}
              </nav>

              <Button
                variant="secondary"
                onClick={handleAdminLogout}
                isLoading={isLoggingOut}
                disabled={isLoggingOut}
                leftIcon={<LogOut className="w-4 h-4" />}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                Logout
              </Button>
            </div>

            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-[#667085] hover:text-[#101828] hover:bg-[#F9FAFB]"
              onClick={toggleMobileMenu}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu-admin"
            >
              <span className="sr-only">
                {isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              </span>
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden" id="mobile-menu-admin">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t border-[#EAECF0]">
              {adminNavLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) =>
                    `block px-3 py-2 rounded-md text-base font-medium ${
                      isActive
                        ? 'text-[#7F56D9] bg-[#F9F5FF]'
                        : 'text-[#667085] hover:text-[#101828] hover:bg-[#F9FAFB]'
                    }`
                  }
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </NavLink>
              ))}
              <Button
                variant="secondary"
                onClick={handleAdminLogout}
                isLoading={isLoggingOut}
                disabled={isLoggingOut}
                leftIcon={<LogOut className="w-4 h-4" />}
                className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 mt-2"
              >
                Logout
              </Button>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;